import { MouseEvent, useEffect, useState } from "react";
import { Flex, Text, Container, Button, ButtonProps, Spinner } from "@/ui";
import SettingsOutlined from "assets/icons/settings_outlined.svg?react";
import ChangeCameraOutlined from "assets/icons/change_camera_outlined.svg?react";
import CameraOffIcon from "assets/icons/camera_off.svg?react";
import CameraOnIcon from "assets/icons/camera.svg?react";
import { t } from "@/utils";
import { useIsMobileView } from "@/hooks";
import environment from "@/modules/environment";
import CameraList from "@/modules/app/components/CameraList";
import MobileMenu from "./MobileMenu";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { useCameraSettings } from "@/modules/cameraSettings/CameraSettingsFlow";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import { useCameraMethods } from "camera/modules/station";
import { useCamera } from "camera/store/camera";
import { getIsPaired } from "camera/store/station/selectors";
import { useStation } from "camera/store/station";

const { appMode } = environment;

export const NavItem = ({
  children,
  onClick,
  as,
  disabled
}: {
  children: any;
  gap?: string;
  as?: ButtonProps["as"];
  onClick?: Cb<MouseEvent>;
  disabled?: boolean;
}) => (
  <Button
    as={as}
    variant="unstyled"
    display="flex"
    alignItems="center"
    columnGap="0.5rem"
    px="16"
    py="8"
    onClick={onClick}
    isDisabled={disabled}
    w="100%"
    justifyContent="flex-start"
  >
    {children}
  </Button>
);

export default function Navbar() {
  const isPaired = useStation(getIsPaired);
  const [openSettings, closeSettings] = useCameraSettings((state) => [state.open, state.close]);
  const changeCamera = useCameraMethods((state) => state.changeCamera);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  // changing settings while recordingMode is OFF will probably break stuff, but maybe not, heh
  // const recordingMode = useStation((state) => state.recordingMode);
  const { cameraList, cameraId, error } = useCamera((state) => ({
    cameraList: state.cameraList,
    cameraId: state.cameraId,
    error: state.error
  }));
  const { changeCameraStationRecordingMode } = useCameraMethods();

  const recordingMode = useStation((state) => state.recordingMode);
  const isCameraOff = recordingMode === "OFF";
  const isMobile = useIsMobileView();

  useEffect(() => {
    const unsub = [
      dataSyncEmitter.on("pause-camera-start", () => setDisabled(true)),
      dataSyncEmitter.on("pause-camera-end", () => setDisabled(false)),
      dataSyncEmitter.on("camera-was-deleted", () => closeSettings())
    ];
    return () => unsub.forEach((cb) => cb());
  });

  const toggleCameraRecordingMode = async () => {
    if (loading || disabled) return;
    dataSyncEmitter.emit("pause-camera-start");
    setLoading(true);
    await changeCameraStationRecordingMode(isCameraOff ? "ON" : "OFF");
    setLoading(false);
    dataSyncEmitter.emit("pause-camera-end");
  };
  return (
    <Container centerContent>
      <Flex align="center" justify="space-between" py="16px" w="100%">
        <Flex alignItems="center" h="40px">
          <Logo />
          <Text size="md" variant="title" fontFamily="body" color="on-surface-variant" mt="0.125rem">
            {appMode !== "story" && " · " + t(appMode)}
          </Text>
        </Flex>
        {isMobile ? (
          <MobileMenu
            isPaired={isPaired}
            cameraList={cameraList}
            cameraId={cameraId}
            changeCamera={(jid) => changeCamera(jid)}
            isCameraOff={isCameraOff}
            loading={loading}
            disabled={disabled}
            hasError={Boolean(error)}
            toggleCameraRecordingMode={toggleCameraRecordingMode}
            openSettings={openSettings}
          />
        ) : (
          <Flex gap="8" align="center">
            <CameraList
              activeCameraId={cameraId}
              cameraList={cameraList}
              changeCamera={(jid) => changeCamera(jid)}
              isDisabled={Boolean(recordingMode === "OFF" || loading)}
            >
              <NavItem as="div" disabled={recordingMode === "OFF"}>
                <Icon fill="on-surface-variant" icon={ChangeCameraOutlined} />
                <Text>{t("changeCamera")}</Text>
              </NavItem>
            </CameraList>
            {isPaired && (
              <NavItem onClick={toggleCameraRecordingMode} disabled={disabled || Boolean(error)}>
                {loading ? (
                  <Spinner width="24px" height="24px" color="on-surface-variant" />
                ) : (
                  <Icon fill="on-surface-variant" icon={isCameraOff ? CameraOnIcon : CameraOffIcon} size="24px" />
                )}
                <Text>{isCameraOff ? t("startCamera") : t("pauseCamera")}</Text>
              </NavItem>
            )}
            {isPaired && (
              <NavItem
                onClick={openSettings}
                // disabled={recordingMode === "OFF"}
              >
                <Icon fill="on-surface-variant" icon={SettingsOutlined} />
                <Text>{t("settings")}</Text>
              </NavItem>
            )}
          </Flex>
        )}
      </Flex>
    </Container>
  );
}
